import {useGetParticipationQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetWorkshopInterestQuery} from '@dms/api/workshop';

import {HeaderPermissions} from '../components/EntityHeader/types/headerPermissions';
import {usePermissions} from './usePermissions/usePermissions';

interface WorkshopInterestEditPermissions {
  interestId: string;
}

export const useWorkshopInterestEditPermissions = (props: WorkshopInterestEditPermissions) => {
  const {data: interestParticipation} = useGetParticipationQuery({
    recordId: props.interestId,
    resourceId: EntityResourceIds.workshopInterest,
  });

  const {data: interestData} = useGetWorkshopInterestQuery({
    id: props.interestId,
  });

  const scope = {
    participation: interestParticipation,
    branchId: interestData?.branchId,
  };

  const [
    hasReadPermission,
    hasCreatePermission,
    hasOpenPermission,
    hasEndPermission,
    hasClosePermission,
    hasReopenPermission,
    hasScheduleVisitPermission,
    hasDeletePermission,
    hasSetNotePermission,
    hasSetSourcePermission,
    hasChangeUtmPermission,
    hasChangeBranchPermission,
    hasChangeCustomerPermission,
    hasSelectContactPersonPermission,
    hasChangeLeadQualityPermission,
    hasEditCustomFieldsPermission,
    hasReadCustomFieldsPermission,
    hasReadCommentsPermission,
    hasAddCommentPermission,
    hasUpdateCommentPermission,
    hasDeleteCommentPermission,
    hasUpdateCustomerRequestPermission,
    hasAddCustomerRequestPermission,
    hasRemoveCustomerRequestPermission,
    hasSelectVehiclePermission,
    hasRemoveVehiclePermission,
    hasUpdateVehicleDetailsPermission,
    hasUpdateVehicleInformationPermission,
    hasReadTagPermission,
    hasAddTagToRecordPermission,
    hasRemoveTagFromRecordPermission,
    hasAssignUserPermission,
    hasAssignOrganizationalUnitPermission,
    hasUnassignUserPermission,
    hasUnassignOrganizationalUnitPermission,
    hasTransferOwnershipToUserPermission,
    hasTransferOwnershipToOrganizationalUnitPermission,
    hasTransferOwnershipToTeamUserPermission,
    hasCreateTagPermission,
    hasAddTagPermission,
    hasUpdateTagPermission,
    hasRemoveTagPermission,
  ] = usePermissions({
    permissionKeys: [
      'workshopInterestRead',
      'workshopInterestCreate',
      'workshopInterestOpen',
      'workshopInterestEnd',
      'workshopInterestClose',
      'workshopInterestReopen',
      'workshopInterestScheduleVisit',
      'workshopInterestDelete',
      'workshopInterestSetNote',
      'workshopInterestSetSource',
      'workshopInterestChangeUtm',
      'workshopInterestChangeBranch',
      'workshopInterestChangeCustomer',
      'workshopInterestSelectContactPerson',
      'workshopInterestChangeLeadQuality',
      'workshopInterestCustomFields',
      'workshopInterestCustomFieldRead',
      'workshopInterestCommentRead',
      'workshopInterestCommentAdd',
      'workshopInterestCommentUpdate',
      'workshopInterestCommentDelete',
      'workshopInterestUpdateCustomerRequest',
      'workshopInterestAddCustomerRequest',
      'workshopInterestRemoveCustomerRequest',
      'workshopInterestSelectVehicle',
      'workshopInterestRemoveVehicle',
      'workshopInterestUpdateVehicleDetails',
      'workshopInterestUpdateVehicleInformation',
      'workshopInterestTagReadRecord',
      'workshopInterestTagAddToRecord',
      'workshopInterestTagRemoveFromRecord',
      'workshopInterestAssignUser',
      'workshopInterestAssignOrganizationalUnit',
      'workshopInterestUnassignUser',
      'workshopInterestUnassignOrganizationalUnit',
      'workshopInterestTransferOwnershipToUser',
      'workshopInterestTransferOwnershipToOrganizationalUnit',
      'workshopInterestTransferOwnershipToTeamUser',
      'workshopInterestTagCreate',
      'workshopInterestTagAdd',
      'workshopInterestTagUpdate',
      'workshopInterestTagRemove',
    ],
    scopes: {
      workshopInterestRead: scope,
      workshopInterestCreate: scope,
      workshopInterestOpen: scope,
      workshopInterestEnd: scope,
      workshopInterestClose: scope,
      workshopInterestReopen: scope,
      workshopInterestScheduleVisit: scope,
      workshopInterestDelete: scope,
      workshopInterestSetNote: scope,
      workshopInterestSetSource: scope,
      workshopInterestChangeUtm: scope,
      workshopInterestChangeBranch: scope,
      workshopInterestChangeCustomer: scope,
      workshopInterestSelectContactPerson: scope,
      workshopInterestChangeLeadQuality: scope,
      workshopInterestCustomFields: scope,
      workshopInterestCommentRead: scope,
      workshopInterestCommentAdd: scope,
      workshopInterestCommentUpdate: scope,
      workshopInterestCommentDelete: scope,
      workshopInterestUpdateCustomerRequest: scope,
      workshopInterestAddCustomerRequest: scope,
      workshopInterestRemoveCustomerRequest: scope,
      workshopInterestSelectVehicle: scope,
      workshopInterestRemoveVehicle: scope,
      workshopInterestUpdateVehicleDetails: scope,
      workshopInterestUpdateVehicleInformation: scope,
      workshopInterestTagReadRecord: scope,
      workshopInterestTagAddToRecord: scope,
      workshopInterestTagRemoveFromRecord: scope,
      workshopInterestAssignUser: scope,
      workshopInterestAssignOrganizationalUnit: scope,
      workshopInterestUnassignUser: scope,
      workshopInterestUnassignOrganizationalUnit: scope,
      workshopInterestTransferOwnershipToUser: scope,
      workshopInterestTransferOwnershipToOrganizationalUnit: scope,
      workshopInterestTransferOwnershipToTeamUser: scope,
    },
  });

  const participation: HeaderPermissions['participation'] = {
    canAssignUser: hasAssignUserPermission && hasAssignOrganizationalUnitPermission,
    canUnassignUser: hasUnassignUserPermission && hasUnassignOrganizationalUnitPermission,
    canChangeOwner:
      hasTransferOwnershipToUserPermission &&
      hasTransferOwnershipToOrganizationalUnitPermission &&
      hasTransferOwnershipToTeamUserPermission,
  };

  const tag: HeaderPermissions['tag'] = {
    isReadable: hasReadTagPermission,
    isAssignable: hasAddTagToRecordPermission,
    isUnassignable: hasRemoveTagFromRecordPermission,
    isCreatable: hasCreateTagPermission && hasAddTagPermission && hasUpdateTagPermission,
    isUpdatable: hasUpdateTagPermission,
    isDeletable: hasRemoveTagPermission,
  };

  return {
    hasReadPermission,
    hasCreatePermission,
    hasOpenPermission,
    hasEndPermission,
    hasClosePermission,
    hasReopenPermission,
    hasScheduleVisitPermission,
    hasDeletePermission,
    hasSetNotePermission,
    hasSetSourcePermission,
    hasChangeUtmPermission,
    hasChangeBranchPermission,
    hasChangeCustomerPermission,
    hasSelectContactPersonPermission,
    hasChangeLeadQualityPermission,
    hasEditCustomFieldsPermission,
    hasReadCustomFieldsPermission,
    hasReadCommentsPermission,
    hasAddCommentPermission,
    hasUpdateCommentPermission,
    hasDeleteCommentPermission,
    hasUpdateCustomerRequestPermission,
    hasAddCustomerRequestPermission,
    hasRemoveCustomerRequestPermission,
    hasSelectVehiclePermission,
    hasRemoveVehiclePermission,
    hasUpdateVehicleDetailsPermission,
    hasUpdateVehicleInformationPermission,
    participation,
    tag,
  };
};
